'use client';

import { Session } from 'next-auth';
import { useState, useEffect } from 'react';
import { Plus, FileText, Calendar, TrendingDown, MoreVertical, Filter, Loader2 } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

interface DocumentSnippet {
    id: string;
    snippet: string;
    currentSnippet: string;
    wordCount: number;
    allWordCount: number;
    originalAiPercentage: number | undefined;
    currentAiPercentage: number | undefined;
    aiReduction: number;
    createdAt: string;
    updatedAt: string;
    hasRewrittenContent: boolean;
    isProcessed: boolean;
}

interface DocumentsResponse {
    documents: DocumentSnippet[];
    pagination: {
        limit: number;
        offset: number;
        hasMore: boolean;
        nextOffset: number | null;
        total: number;
    };
}

export function LoggedInHome({ session }: { session: Session }) {
    const [documents, setDocuments] = useState<DocumentSnippet[]>([]);
    const [loading, setLoading] = useState(true);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState<number | null>(null);
    const [creatingAIHumanizerDoc, setCreatingAIHumanizerDoc] = useState(false);
    const router = useRouter();

    // Use session.siteId if available, otherwise fall back to test-site-id
    const siteId = (session as any)?.siteId || '87c2d73e-7030-4048-9311-0eb6f66d1e60';

    const fetchDocuments = async (offset: number = 0, append: boolean = false) => {
        try {
            setLoading(!append); // Only show loading for initial fetch
            const response = await fetch(`/api/sites/${siteId}/aihumanit/documents?limit=12&offset=${offset}`);

            if (response.ok) {
                const data: DocumentsResponse = await response.json();

                if (append) {
                    setDocuments(prev => [...prev, ...data.documents]);
                } else {
                    setDocuments(data.documents);
                }

                setHasMore(data.pagination.hasMore);
                setNextOffset(data.pagination.nextOffset);
            } else {
                console.error('Failed to fetch documents:', response.status);
            }
        } catch (error) {
            console.error('Error fetching documents:', error);
        } finally {
            setLoading(false);
        }
    };

    const createAIHumanizerDocument = async () => {
        setCreatingAIHumanizerDoc(true);
        try {
            const response = await fetch(`/api/sites/${siteId}/aihumanit/documents`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text: '', // Start with empty document
                    chatId: null // No chat association
                }),
            });

            if (response.ok) {
                const data = await response.json();
                // Navigate to the AI Humanizer editor
                router.push(`/documents/${data.documentId}`);
            } else {
                const errorData = await response.json();
                console.error('Failed to create AI Humanizer document:', response.status, errorData);
            }
        } catch (error) {
            console.error('Error creating AI Humanizer document:', error);
        } finally {
            setCreatingAIHumanizerDoc(false);
        }
    };

    const loadMoreDocuments = () => {
        if (hasMore && nextOffset !== null && !loading) {
            fetchDocuments(nextOffset, true);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [siteId]);

    const filteredDocuments = documents;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Template Gallery Section */}
                <div className="mb-8">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Start a new document</h2>

                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                        {/* Create New AI Humanizer Document */}
                        <button
                            onClick={createAIHumanizerDocument}
                            disabled={creatingAIHumanizerDoc}
                            className="group flex flex-col items-center p-4 bg-white border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <div className="w-16 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                                {creatingAIHumanizerDoc ? (
                                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                                ) : (
                                    <Plus className="w-8 h-8 text-white" />
                                )}
                            </div>
                            <span className="text-sm font-medium text-gray-900 group-hover:text-purple-700">
                                {creatingAIHumanizerDoc ? 'Creating...' : 'New AI Humanizer'}
                            </span>
                            <span className="text-xs text-gray-500 text-center">Transform AI text to human-like content</span>
                        </button>
                    </div>
                </div>

                {/* Recent Documents Section */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-medium text-gray-900">Recent documents</h2>
                        <div className="flex items-center gap-2">
                            <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                                <Filter className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                            {[...Array(12)].map((_, i) => (
                                <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
                                    <div className="w-full h-20 bg-gray-200 rounded mb-3"></div>
                                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                                </div>
                            ))}
                        </div>
                    ) : filteredDocuments.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No documents yet
                            </h3>
                            <p className="text-gray-500 mb-4">
                                Create your first document to get started
                            </p>
                            <button
                                onClick={createAIHumanizerDocument}
                                disabled={creatingAIHumanizerDoc}
                                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {creatingAIHumanizerDoc ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                                        Creating...
                                    </>
                                ) : (
                                    'Create AI Humanizer Document'
                                )}
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                                {filteredDocuments.map((doc) => (
                                    <Link
                                        key={doc.id}
                                        href={`/documents/${doc.id}`}
                                        className="group bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                                    >
                                        <div className="p-4">
                                            {/* Document Preview */}
                                            <div className="w-full h-20 bg-gray-50 border border-gray-200 rounded mb-3 p-2 overflow-hidden relative">
                                                <div className="text-xs text-gray-600 leading-tight">
                                                    {doc.snippet}
                                                </div>
                                                {doc.isProcessed && (
                                                    <div className="absolute top-1 right-1">
                                                        <div className="bg-green-500 w-2 h-2 rounded-full"></div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Document Info */}
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">
                                                        {doc.snippet.length > 30 ? doc.snippet.substring(0, 30) + '...' : doc.snippet}
                                                    </p>
                                                    <div className="flex items-center text-xs text-gray-500 mt-1">
                                                        <Calendar className="w-3 h-3 mr-1" />
                                                        {new Date(doc.updatedAt).toLocaleDateString()}
                                                    </div>
                                                    {doc.aiReduction > 0 && (
                                                        <div className="flex items-center text-xs text-green-600 mt-1">
                                                            <TrendingDown className="w-3 h-3 mr-1" />
                                                            -{doc.aiReduction}% AI
                                                        </div>
                                                    )}
                                                </div>
                                                <button className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 transition-opacity">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            {/* Load More */}
                            {hasMore && (
                                <div className="text-center mt-8">
                                    <button
                                        onClick={loadMoreDocuments}
                                        disabled={loading}
                                        className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                    >
                                        {loading ? 'Loading...' : 'Load More'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>


        </div>
    );
}